import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { GoogleLogo, SignIn, Lock } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../auth/AuthContext';
import raiseysLogo from '../assets/raiseys-logo.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dev auto-login only ever fires on preview/local hosts — production domains
// always get the normal login screen (backend endpoint is env-gated too).
const IS_DEV_HOST = typeof window !== 'undefined'
  && (window.location.hostname.endsWith('.preview.emergentagent.com')
      || window.location.hostname === 'localhost');

const LoginPage = () => {
  const { user, login, logout, devLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [staffOnly, setStaffOnly] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  // Set true the moment a fresh sign-in succeeds on this page (form or Google).
  // Used to distinguish "just logged in → navigate home" from "navigated here
  // while already authed → show switch-account card".
  const [justSignedIn, setJustSignedIn] = useState(false);
  // Autofill defence: inputs are `readOnly` for the first 200ms after mount, so that
  // aggressive browser autofill (which fires during initial render) sees fields it
  // cannot write into. We then drop the attribute, allowing normal typing.
  const [acceptInput, setAcceptInput] = useState(false);
  // Dev auto-login (preview only): skipped after an explicit sign-out so
  // testers can still switch accounts.
  const [devChecking, setDevChecking] = useState(
    IS_DEV_HOST && sessionStorage.getItem('raisey_dev_manual_logout') !== '1'
  );
  const devAttempted = useRef(false);

  useEffect(() => {
    if (!devChecking || devAttempted.current) return;
    if (user === null) return; // wait for the /me probe to resolve
    if (user && user.id) { setDevChecking(false); return; } // already signed in
    devAttempted.current = true;
    devLogin()
      .then(() => setJustSignedIn(true))
      .catch(() => {})
      .finally(() => setDevChecking(false));
  }, [user, devChecking, devLogin]);

  useEffect(() => {
    axios.get(`${API}/auth/config`).then((r) => setStaffOnly(!!r.data.staff_only_mode)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAcceptInput(true), 200);
    return () => clearTimeout(t);
  }, []);

  // Dev auto-login in flight — show a brief splash instead of flashing the form.
  if (devChecking && !(user && user.id)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6">
        <div className="text-center" data-testid="dev-autologin-splash">
          <img src={raiseysLogo} alt="Raisey's logo" className="h-24 w-auto mx-auto mb-4 opacity-80" />
          <p className="text-sm text-text-secondary animate-pulse">Signing you in…</p>
        </div>
      </div>
    );
  }

  // Just-logged-in → straight home.
  if (user && user.id && justSignedIn) {
    return <Navigate to="/" replace />;
  }

  // Hit /login while already signed in → show a switch-account card.
  if (user && user.id && !switchingAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6">
        <div className="w-full max-w-md bg-surface border border-border-color rounded-2xl p-8 shadow-2xl text-center" data-testid="already-signed-in-card">
          <div className="flex flex-col items-center gap-3 mb-4">
            <img src={raiseysLogo} alt="Raisey's logo" className="h-24 w-auto" data-testid="already-signed-in-logo" />
            <h1 className="text-3xl font-bold font-heading text-gold">Raisey&apos;s Smart Saver App</h1>
          </div>
          <p className="text-sm text-text-secondary mb-1">You&apos;re already signed in as</p>
          <p className="text-text-primary font-semibold mb-6" data-testid="already-signed-in-email">
            {user.email} <span className="text-xs text-gold uppercase ml-1">{user.role}</span>
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid="continue-as-current-user"
              onClick={() => { window.location.href = '/'; }}
              className="w-full px-4 py-2.5 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
            >
              Continue as {user.name || user.email}
            </button>
            <button
              type="button"
              data-testid="sign-out-to-switch"
              onClick={async () => { await logout(); setSwitchingAccount(true); }}
              className="w-full px-4 py-2.5 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
            >
              Sign out & sign in as a different user
            </button>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      setJustSignedIn(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6">
      <div className="w-full max-w-md bg-surface border border-border-color rounded-2xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <img src={raiseysLogo} alt="Raisey's logo" className="h-28 w-auto mx-auto mb-4" data-testid="login-logo" />
          <h1 className="text-3xl font-bold font-heading text-gold mb-1">Raisey&apos;s Smart Saver App</h1>
          <p className="text-sm text-text-secondary">Raisey&apos;s Smart Saver — sign in to continue</p>
        </div>

        {staffOnly && (
          <div
            data-testid="staff-only-banner"
            className="mb-6 flex items-start gap-2 px-3 py-2.5 rounded-lg border border-gold/40 bg-gold/[0.06] text-xs text-text-primary"
          >
            <Lock size={16} weight="duotone" className="text-gold mt-0.5 shrink-0" />
            <span>
              <strong className="text-gold">Internal testing mode.</strong> Only admin and sales rep
              accounts can sign in right now. Customer access will open once testing is complete.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="login-form" autoComplete="off">
          {/* Honeypot inputs catch aggressive browser autofill that ignores autoComplete="off". */}
          <input type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} readOnly />
          <input type="password" name="password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} readOnly />
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              name="raisey_email"
              autoComplete="off"
              readOnly={!acceptInput}
              className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              name="raisey_password"
              autoComplete="off"
              readOnly={!acceptInput}
              className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-danger bg-danger/10 border border-danger/30 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50"
          >
            <SignIn size={18} weight="bold" />
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-border-color" />
          <span className="text-xs text-text-secondary">OR</span>
          <div className="h-px flex-1 bg-border-color" />
        </div>

        <button
          type="button"
          onClick={googleSignIn}
          data-testid="login-google-button"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
        >
          <GoogleLogo size={18} weight="bold" />
          Sign in with Google
        </button>

        <p className="mt-6 text-xs text-text-secondary text-center">
          Accounts are provisioned by your administrator.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
